import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Layers } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import TopTopicsBlock from '../components/trending/TopTopicsBlock';
import { usePageSeo } from '../hooks/usePageSeo';
import {
  useHowItWorksTopics,
  resolveScopeName,
  topicsInScope,
  topicToTrendingItem,
  TOP_TOPICS_N,
} from '../lib/howItWorksHierarchy';

// Section (e.g. /how-it-works/section/ee-architecture) and subsection (e.g.
// /how-it-works/section/ee-architecture/communication) landing pages share this one
// component — both just show that scope's top trending topics plus a "More Topics"
// link, the scope only differs by whether `subcategorySlug` is present. Any future
// category/subcategory is reachable here automatically, since the scope is resolved
// purely from the existing topic catalog's `category`/`subcategory` strings.
const HowItWorksSectionPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const navigate = useNavigate();
  const { topics, loading } = useHowItWorksTopics();

  const { categoryName, subcategoryName } = resolveScopeName(topics, categorySlug, subcategorySlug);
  const scopeTopics = useMemo(
    () => topicsInScope(topics, categorySlug, subcategorySlug),
    [topics, categorySlug, subcategorySlug]
  );
  const scopeName = subcategoryName || categoryName;

  useEffect(() => {
    if (!loading && topics.length > 0 && !categoryName) {
      navigate('/how-it-works', { replace: true });
    }
  }, [loading, topics.length, categoryName, navigate]);

  usePageSeo(
    scopeName ? `${scopeName} | How Does It Work? | AUTOXIUM` : undefined,
    scopeName ? `Explore ${scopeName} topics on AUTOXIUM.` : undefined
  );

  const items = useMemo(() => scopeTopics.map(topicToTrendingItem), [scopeTopics]);
  const hasMore = scopeTopics.length > TOP_TOPICS_N;
  const moreTopicsPath = `/how-it-works/section/${categorySlug}${
    subcategorySlug ? `/${subcategorySlug}` : ''
  }/more`;

  if (loading || !scopeName) {
    return (
      <div className="min-h-screen" data-testid="how-it-works-section-loading">
        <div className="bg-chassis text-white py-16 md:py-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
            <Skeleton className="h-4 w-40 mb-6 bg-white/10" />
            <Skeleton className="h-10 w-1/2 bg-white/10" />
          </div>
        </div>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="how-it-works-section-page">
      <div className="bg-chassis text-white py-16 md:py-20">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-6"
            data-testid="back-to-how-it-works"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to How Does It Work?
          </Link>
          <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">
            {subcategoryName ? `${categoryName} / Subsection` : 'Section'}
          </p>
          <h1 className="font-chivo text-3xl md:text-5xl font-black uppercase tracking-tighter">
            {scopeName}
          </h1>
        </div>
      </div>

      {scopeTopics.length === 0 ? (
        <div className="text-center py-16" data-testid="how-it-works-section-empty">
          <Layers className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-chivo text-xl font-bold uppercase mb-2">No Topics Yet</h3>
          <p className="text-muted-foreground">Check back soon for new system breakdowns.</p>
        </div>
      ) : (
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <TopTopicsBlock
            sectionType="how_it_works"
            items={items}
            hasMore={hasMore}
            moreTopicsPath={moreTopicsPath}
          />
        </div>
      )}
    </div>
  );
};

export default HowItWorksSectionPage;
